<script lang="ts" setup>
import { ref, watchEffect, PropType, computed } from 'vue'
import { UIInput, UIButton } from '@gohighlevel/ghl-ui'
import { useValidation } from '../../../common/composable/validation'

import { ICustomField, ICustomValue, Menu } from '../types'
import { hasHandleBar, isValidHandleBars } from '../../../common/utils/helper'

import CustomVariablePicker from '../../../common/components/CustomVariablePicker.vue'
import { useService } from '../service'
import { ActionTypes } from '../store/actions'
import { useStore } from '../store'
import i18n from 'common/locales'
import { useNotificationEvent } from 'common/composition/useNotificationEvent'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },

  userId: { type: String, required: true },
  templateId: { type: String, required: true },
  locationId: { type: String, required: true },
  customFieldsLocation: {
    type: Array as PropType<Array<ICustomField>>,
    default: [],
  },
  customValuesLocation: {
    type: Array as PropType<Array<ICustomValue>>,
    default: [],
  },
})
const emit = defineEmits(['close'])
const templateData = computed(() => store.state.templateData)
const store = useStore()
const showContent = ref(false)
const fromName = ref<string>(templateData.value?.fromName as string)
const fromEmail = ref<string>(templateData.value?.fromAddress as string)
const previewText = ref<string>(templateData.value?.previewText as string)
const emailSubject = ref<string>(templateData.value?.subjectLine as string)
const sendEmailLoader = ref(false)
const { validateEmail } = useValidation()
const service = useService()
const { enableNotification } = useNotificationEvent()

function onClickClose() {
  const pickerEl = document.querySelector('.dropdown-tree__container')
  if (pickerEl) return
  showContent.value = false
  setTimeout(() => {
    emit('close')
  }, 500)
}
watchEffect(() => {
  showContent.value = props.show
})

async function onClickSave() {
  sendEmailLoader.value = true
  try {
    const { data: newTemplateData } = await service.updateTemplate(
      props.templateId,
      {
        fromName: fromName.value,
        subjectLine: emailSubject.value,
        fromAddress: fromEmail.value,
        previewText: previewText.value,
        locationId: props.locationId,
        updatedBy: props.userId,
      }
    )
    store.dispatch(ActionTypes.UPDATE_TEMPLATE, newTemplateData)
    enableNotification({
      title: i18n.global.t('emailTemplate.settingsSaved'),
      content: i18n.global.t('emailTemplate.settingsSavedHelper'),
      type: 'success',
    })
    onClickClose()
  } catch (error: any) {
    enableNotification({
      title: i18n.global.t('common.somethingWentWrong'),
      content: i18n.global.t(`common.failedToSaveSettingsHelper`),
      type: 'error',
    })
  } finally {
    sendEmailLoader.value = false
  }
}

function updateFromEmail(payload: Menu) {
  if (payload.value) fromEmail.value = payload.value
}

function updateSubject(payload: Menu) {
  if (payload.value) emailSubject.value = payload.value
}

function updateFromName(payload: Menu) {
  if (payload.value) fromName.value = payload.value
}
function updatePreviewText(payload: Menu) {
  if (payload.value) previewText.value = payload.value
}
const isValidInput = computed(() => true)
</script>

<style scoped></style>

<template>
  <div
    id="pg-email-builder-template-setting"
    class="fixed inset-0 overflow-hidden"
  >
    <div class="absolute inset-0 overflow-hidden">
      <section
        class="absolute inset-y-0 right-0 pl-10 max-w-full flex sm:pl-16"
      >
        <transition
          enter-active-class="transform transition ease-in-out duration-500 sm:duration-700"
          enter-class="translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transform transition ease-in-out duration-500 sm:duration-700"
          leave-class="translate-x-0"
          leave-to-class="translate-x-full"
        >
          <div
            class="w-screen max-w-lg"
            v-if="showContent"
            v-click-outside="showContent && onClickClose"
          >
            <div
              class="h-full flex flex-col bg-white shadow-xl overflow-y-scroll"
            >
              <div class="flex-1">
                <!-- Header -->
                <header class="px-4 py-6 sm:px-6 border-b border-gray-200">
                  <div class="flex items-start justify-between space-x-3">
                    <div class="space-y-1">
                      <h2 class="text-2xl leading-7 font-medium mb-2">
                        {{ $t('emailTemplate.settingHeading') }}
                      </h2>
                      <p class="text-sm text-gray-400 leading-4">
                        {{ $t('emailTemplate.settingHeadingHelper') }}
                      </p>
                    </div>
                    <div class="h-7 flex items-center">
                      <button
                        @click="onClickClose"
                        aria-label="Close panel"
                        class="text-gray-400 hover:text-gray-500 transition ease-in-out duration-150"
                      >
                        <!-- Heroicon name: x -->
                        <svg
                          class="h-6 w-6"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </header>

                <!-- Divider container -->
                <div class="py-0 space-y-0">
                  <div
                    class="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-1 sm:gap-5 padding-x-6 padding-y-5"
                  >
                    <div>
                      <div>
                        <label
                          for="from_name"
                          class="block text-sm font-medium leading-5 text-gray-500 sm:mt-px sm:pt-2"
                        >
                          {{ $t('campaignSchedule.fromNameInput') }}
                        </label>
                      </div>
                      <div class="sm:col-span-2 mt-2">
                        <div class="rounded-md shadow-sm">
                          <div class="relative">
                            <UIInput
                              id="pg-email-builder-template-setting-from-email"
                              name="from_name"
                              v-model="fromName"
                              :placeholder="$t('common.name')"
                              class="pr-9 text-gray-400"
                            />
                            <CustomVariablePicker
                              id="__template-setting-from-name"
                              @update:selectedValue="updateFromName"
                              :noMarginTop="true"
                              :customFieldsLocation="customFieldsLocation"
                              :customValuesLocation="customValuesLocation"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Project name -->
                  <div
                    class="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-1 sm:gap-5 padding-x-6 padding-y-5"
                  >
                    <div>
                      <div>
                        <label
                          for="from_email"
                          class="block text-sm font-medium leading-5 text-gray-500 sm:mt-px sm:pt-2"
                        >
                          {{ $t('campaignSchedule.fromEmailInput') }}
                        </label>
                      </div>
                      <div class="sm:col-span-2 mt-2">
                        <div class="rounded-md shadow-sm">
                          <div class="relative">
                            <UIInput
                              id="pg-email-builder-template-setting-from-email"
                              name="from_email"
                              v-model="fromEmail"
                              placeholder="example@mail.com"
                              :error="
                                fromEmail
                                  ? !(
                                      validateEmail(fromEmail) ||
                                      (hasHandleBar(fromEmail) &&
                                        isValidHandleBars(fromEmail))
                                    )
                                  : false
                              "
                            />
                            <CustomVariablePicker
                              id="__template-setting-from-email"
                              @update:selectedValue="updateFromEmail"
                              :noMarginTop="true"
                              :customFieldsLocation="customFieldsLocation"
                              :customValuesLocation="customValuesLocation"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-1 sm:gap-4 padding-x-6 padding-y-5"
                  >
                    <div>
                      <label
                        for="project_description"
                        class="block text-sm font-medium leading-5 text-gray-500 sm:mt-px sm:pt-2"
                      >
                        {{ $t('common.emailSubject') }}
                      </label>
                    </div>
                    <div class="sm:col-span-2">
                      <div class="flex rounded shadow-sm">
                        <div class="relative w-full">
                          <UIInput
                            type="textarea"
                            :placeholder="$t('common.subject')"
                            v-model="emailSubject"
                            id="email_subject_line"
                            :rows="3"
                          />
                          <CustomVariablePicker
                            id="__template-setting-email-subject"
                            class="items-start -top-10"
                            @update:selectedValue="updateSubject"
                            :noMarginTop="true"
                            :customFieldsLocation="customFieldsLocation"
                            :customValuesLocation="customValuesLocation"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-1 sm:gap-5 padding-x-6 padding-y-5"
                  >
                    <div>
                      <div>
                        <label
                          for="preview_text"
                          class="block text-sm font-medium leading-5 text-gray-500 sm:mt-px sm:pt-2"
                        >
                          {{ $t('campaignSchedule.previewText') }}
                        </label>
                      </div>
                      <div class="sm:col-span-2 mt-2">
                        <div class="rounded-md shadow-sm">
                          <div class="relative">
                            <UIInput
                              id="pg-email-builder-template-setting-preview-text"
                              name="preview_text"
                              :placeholder="$t('campaignSchedule.previewText')"
                              v-model="previewText"
                            />
                            <CustomVariablePicker
                              id="__template-setting-preview-text"
                              @update:selectedValue="updatePreviewText"
                              :noMarginTop="true"
                              :customFieldsLocation="customFieldsLocation"
                              :customValuesLocation="customValuesLocation"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Action buttons -->
              <div class="flex-shrink-0 px-4 bg-gray-50 py-6 sm:px-6">
                <div class="space-x-3 flex justify-end">
                  <span class="inline-flex rounded-md shadow-sm">
                    <UIButton
                      id="pg-email-builder-template-setting-cancel-btn"
                      @click="onClickClose"
                      size="large"
                    >
                      {{ $t('common.cancel') }}
                    </UIButton>
                  </span>
                  <span class="inline-flex rounded-md shadow-sm">
                    <UIButton
                      id="pg-email-builder-template-setting-save-btn"
                      @click="onClickSave"
                      :class="{
                        'cursor-not-allowed': !isValidInput,
                        'bg-curious-blue-500 text-white hover:bg-curious-blue-400':
                          isValidInput,
                        'pointer-events-none': sendEmailLoader,
                      }"
                      type="primary"
                      :disabled="!isValidInput"
                      :loading="sendEmailLoader"
                      size="large"
                    >
                      {{ $t('common.save') }}
                    </UIButton>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </section>
    </div>
  </div>
</template>
<style>
.padding-x-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
.padding-y-5 {
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
}
#pg-email-builder-template-setting .n-input {
  font-family: 'Inter' !important;
}
#pg-email-builder-template-setting .n-input__placeholder {
  color: rgba(194, 194, 194, 1) !important;
}
</style>
